// Filter types & ids that color-code can't render (mirrors generateColorCodeOptions).
// "documentsType" (the Documents filter) is deliberately absent — see the note in
// generateColorCodeOptions for why colour-coding by it has never worked.
const SUPPORTED_FILTER_TYPES = ["tagType", "valuePathType", "nestedRollupTagType"];
const UNSUPPORTED_FILTER_IDS = [
  "annoAuthorFilter",
  "authorFilter",
  "publishedDateFilter",
  "annoEnabledBlockValuePathFilter",
];

const isSupportedLeaf = (cfg) =>
  SUPPORTED_FILTER_TYPES.includes(cfg?.target?.filterType) &&
  !UNSUPPORTED_FILTER_IDS.includes(cfg?.filterId);

/**
 * Walks the V3 filterConfigs and strips unsupported filter ids/types out of
 * the hierarchy wrappers' commonFilters arrays. Direct (non-hierarchy) leaves
 * that don't pass `isSupportedLeaf` are dropped too. Hierarchy levels (themes,
 * sub-themes, tags) are kept untouched.
 */
export const cleanFilterConfigsForColorCode = (configs) => {
  if (!Array.isArray(configs)) return configs;

  return configs
    .map((c) => {
      // Recurse into nested sections.
      if (Array.isArray(c?.configs)) {
        return { ...c, configs: cleanFilterConfigsForColorCode(c.configs) };
      }

      // annoHierarchyType wrapper — drop unsupported entries from commonAnnotationFilters.
      if (c?.source?.filterType === "annoHierarchyType") {
        return {
          ...c,
          source: {
            ...c.source,
            commonAnnotationFilters: (c.source.commonAnnotationFilters || []).filter(isSupportedLeaf),
          },
        };
      }

      // docHierarchyType wrapper — drop unsupported entries from every nested
      // filter list it carries: commonFilters, commonDocumentFilters, each
      // sharedAcrossMultiple group's filters, and each perDataset's filters
      // and rollups (the latter is where rollupValuePathType lives).
      if (c?.source?.filterType === "docHierarchyType") {
        return {
          ...c,
          source: {
            ...c.source,
            commonDocumentFilters: (c.source.commonDocumentFilters || []).filter(isSupportedLeaf),
            commonFilters: (c.source.commonFilters || []).filter(isSupportedLeaf),
            sharedAcrossMultiple: (c.source.sharedAcrossMultiple || []).map((g) => ({
              ...g,
              filters: (g?.filters || []).filter(isSupportedLeaf),
            })),
            perDataset: (c.source.perDataset || []).map((d) => ({
              ...d,
              filters: (d?.filters || []).filter(isSupportedLeaf),
              rollups: (d?.rollups || []).filter(isSupportedLeaf),
              docFieldFilter: null,
            })),
          },
        };
      }

      // Plain leaf filter — keep only if supported.
      return isSupportedLeaf(c) ? c : null;
    })
    .filter(Boolean);
};
